import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageMeta from "@/components/feature/PageMeta";
import SectionHeader from "@/components/feature/SectionHeader";
import PosterCard from "@/components/feature/PosterCard";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryEntries from "@/pages/home/components/CategoryEntries";
import FeaturedCollections from "@/pages/home/components/FeaturedCollections";
import { getLatest, getHot, typeLabels } from "@/lib/catalog";

const tabs = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "drama", label: "电视剧" },
] as const;

export default function Home() {
  const navigate = useNavigate();
  const [tab, setTab] = useState<"all" | "movie" | "drama">("all");
  const [keyword, setKeyword] = useState("");

  const latestAll = getLatest();
  const latest = tab === "all" ? latestAll : latestAll.filter((t) => t.type === tab);
  const visible = latest.slice(0, 8);

  const hotList = getHot(6);
  const topHot = hotList[0];

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  return (
    <>
      <PageMeta
        title="日韩在线-最新日韩电影电视剧在线观看平台"
        description="日韩在线为您提供丰富的日韩最新电影、电视剧资源，涵盖热门影视剧集，支持在线观看，满足用户多样的观影需求。"
        keywords="日韩在线,日韩影视,日韩电影,日韩电视剧,在线观看日韩影视,日韩剧集,日韩电影推荐"
      />
      <SiteLayout>
        <h1 className="sr-only">日韩在线 - 最新日韩电影电视剧在线观看平台</h1>
        <HeroCarousel />

        {/* Search + stats bar */}
        <section className="w-full px-4 md:px-8 -mt-8 md:-mt-10 relative z-20">
          <div className="rounded-lg bg-background-50 border border-background-200/80 px-4 md:px-6 py-4 md:py-5 flex flex-col lg:flex-row lg:items-center gap-4">
            <form onSubmit={submitSearch} className="flex items-center gap-2 flex-1">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-base w-4 h-4 flex items-center justify-center" />
                <input
                  type="search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索影视名称、主演、导演…"
                  aria-label="搜索影视"
                  className="w-full h-11 pl-10 pr-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
                />
              </div>
              <button
                type="submit"
                className="h-11 px-4 md:px-5 rounded-md bg-primary-600 text-background-50 text-sm font-label font-semibold whitespace-nowrap hover:bg-primary-700 transition-colors cursor-pointer"
              >
                搜索
              </button>
            </form>
            <div className="flex items-center gap-5 md:gap-7 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="font-label text-lg font-bold text-foreground-950">{latestAll.length}</span>
                <span className="text-xs text-foreground-500">部作品</span>
              </div>
              <span className="hidden md:block w-px h-6 bg-background-200" />
              <div className="flex items-center gap-2">
                <i className="ri-movie-line text-primary-500 text-base w-4 h-4 flex items-center justify-center" />
                <span className="text-xs text-foreground-600">
                  电影 {latestAll.filter((t) => t.type === "movie").length}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-tv-2-line text-secondary-500 text-base w-4 h-4 flex items-center justify-center" />
                <span className="text-xs text-foreground-600">
                  电视剧 {latestAll.filter((t) => t.type === "drama").length}
                </span>
              </div>
              <Link
                to="/favorites"
                className="hidden lg:inline-flex items-center gap-1.5 text-xs font-label font-semibold text-accent-700 hover:text-accent-800 whitespace-nowrap"
              >
                <i className="ri-heart-3-line w-4 h-4 flex items-center justify-center" />
                我的收藏
              </Link>
            </div>
          </div>
        </section>

        {/* Category entries */}
        <section className="w-full px-4 md:px-8 pt-12 md:pt-16">
          <SectionHeader
            eyebrow="Quick Entry"
            title="快速进入分类"
            description="按电影或电视剧分类浏览，筛选年份、地区与热门度，找到下一部想看的作品。"
          />
          <CategoryEntries />
        </section>

        {/* Latest titles */}
        <section className="w-full px-4 md:px-8 pt-12 md:pt-16">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6 md:mb-8">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="w-6 h-[3px] rounded-full bg-accent-500" />
                <span className="text-[11px] font-label font-semibold tracking-[0.2em] uppercase text-accent-700">
                  Latest Release
                </span>
              </div>
              <h2 className="font-heading text-xl md:text-2xl font-black text-foreground-950 tracking-tight">
                最新上线影视
              </h2>
              <p className="mt-2 text-sm text-foreground-600">按上线时间排序，第一时间掌握日韩新作动态。</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200/70">
                {tabs.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTab(t.key)}
                    className={`px-4 py-1.5 rounded-full text-xs font-label font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                      tab === t.key
                        ? "bg-primary-600 text-background-50"
                        : "text-foreground-600 hover:text-foreground-950"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              <Link
                to={`/category/${tab === "all" ? "movie" : tab}`}
                className="inline-flex items-center gap-1.5 text-sm font-label font-semibold text-primary-600 hover:text-primary-700 whitespace-nowrap ml-1"
              >
                查看全部
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
              </Link>
            </div>
          </div>

          {visible.length === 0 ? (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
              <i className="ri-file-list-3-line text-3xl text-foreground-300 w-8 h-8 flex items-center justify-center mx-auto" />
              <p className="mt-3 text-sm text-foreground-600">暂无影视资源，请稍后再试</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-7 md:gap-x-5 md:gap-y-9">
              {visible.map((item) => (
                <PosterCard key={item.id} item={item} />
              ))}
            </div>
          )}
        </section>

        {/* Hot ranking */}
        <section className="w-full px-4 md:px-8 pt-14 md:pt-20">
          <div className="rounded-lg bg-background-100 border border-background-200/70 p-4 md:p-8">
            <SectionHeader
              eyebrow="Trending Now"
              title="热门影视排行榜"
              description="根据站内热度与讨论度综合排序，看看大家都在追什么。"
              action={{ label: "进入电影分类", to: "/category/movie" }}
            />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-8">
              {topHot && (
                <Link
                  to={`/detail/${topHot.id}`}
                  className="lg:col-span-5 relative rounded-lg overflow-hidden group h-[260px] md:h-full min-h-[260px] bg-foreground-950"
                >
                  <img
                    src={topHot.backdrop}
                    alt={`${topHot.title} 剧照`}
                    title={`${topHot.title} 在线观看日韩影视`}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/45 to-foreground-950/10" />
                  <div className="absolute top-4 left-4">
                    <span className="px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-[11px] font-label font-bold">
                      TOP 1 · 热度 {topHot.heat}
                    </span>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 p-4 md:p-6">
                    <h3 className="font-heading text-xl md:text-2xl font-black text-background-50">{topHot.title}</h3>
                    <p className="mt-1 text-xs text-background-100/70">
                      {typeLabels[topHot.type]} · {topHot.region} · {topHot.year} · {topHot.genre.join(" / ")}
                    </p>
                    <p className="mt-2.5 text-[13px] text-background-100/80 leading-relaxed line-clamp-2">
                      {topHot.synopsis}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-background-50 text-foreground-950 text-xs font-label font-semibold">
                      <i className="ri-play-fill w-3.5 h-3.5 flex items-center justify-center" />
                      查看详情
                    </span>
                  </div>
                </Link>
              )}

              <div className="lg:col-span-7 flex flex-col divide-y divide-background-200">
                {hotList.slice(1).map((item, i) => (
                  <Link
                    key={item.id}
                    to={`/detail/${item.id}`}
                    className="flex items-center gap-3 md:gap-4 py-3.5 group first:pt-0 last:pb-0"
                  >
                    <span
                      className={`w-7 md:w-8 shrink-0 text-center font-label text-lg md:text-xl font-black ${
                        i === 0 ? "text-accent-600" : "text-foreground-300"
                      }`}
                    >
                      {String(i + 2).padStart(2, "0")}
                    </span>
                    <div className="w-12 h-16 md:w-14 md:h-[76px] shrink-0 rounded-md overflow-hidden bg-background-200">
                      <img
                        src={item.poster}
                        alt={`${item.title} 海报`}
                        title={`${item.title} 在线观看`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-heading text-sm md:text-[15px] font-bold text-foreground-950 line-clamp-1 group-hover:text-primary-600 transition-colors">
                        {item.title}
                      </h3>
                      <p className="mt-1 text-[11px] md:text-xs text-foreground-500 line-clamp-1">
                        {typeLabels[item.type]} · {item.region} · {item.year} · {item.genre.slice(0, 2).join(" / ")}
                      </p>
                      <p className="mt-1 text-[11px] md:text-xs text-foreground-600 line-clamp-1">
                        主演：{item.cast.slice(0, 2).join("、")}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        <i className="ri-star-fill text-accent-500 text-[11px] w-3 h-3 flex items-center justify-center" />
                        <span className="font-label text-sm font-bold text-foreground-900">{item.rating.toFixed(1)}</span>
                      </div>
                      <span className="text-[11px] text-foreground-400 font-label">热度 {item.heat}</span>
                    </div>
                    <i className="ri-arrow-right-s-line text-foreground-300 w-4 h-4 flex items-center justify-center shrink-0" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Collections */}
        <section className="w-full px-4 md:px-8 pt-14 md:pt-20">
          <SectionHeader
            eyebrow="Curated Lists"
            title="热门影视推荐专题"
            description="按主题整理的精选片单，从本季必追到治愈慢生活，一站看全。"
            action={{ label: "浏览全部电视剧", to: "/category/drama" }}
          />
          <FeaturedCollections />
        </section>

        {/* Site intro */}
        <section className="w-full px-4 md:px-8 pt-14 md:pt-20 pb-14 md:pb-20">
          <div className="rounded-lg bg-primary-950 text-background-100 p-6 md:p-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <span className="text-[11px] font-label font-semibold tracking-[0.2em] uppercase text-accent-400">
                About JK Online
              </span>
              <h2 className="mt-3 font-heading text-xl md:text-3xl font-black text-background-50 leading-snug">
                专注日韩影视，把最新剧集信息整理给你
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-background-200/85 max-w-2xl">
                日韩在线持续整理日本与韩国最新电影、电视剧的详细信息，包括剧情简介、主演阵容、上映年份与热度排行。
                你可以通过分类筛选、关键词搜索快速定位想看的作品，也可以把喜欢的影视加入收藏，随时回来看。
                本站所有播放画面仅为前端展示示范，不提供真实播放与下载服务。
              </p>
              <div className="mt-6 flex items-center gap-3 flex-wrap">
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-accent-500 text-background-50 text-xs font-label font-semibold whitespace-nowrap hover:bg-accent-600 transition-colors"
                >
                  了解关于我们
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
                </Link>
                <Link
                  to="/category/drama"
                  className="inline-flex items-center gap-2 h-10 px-5 rounded-md border border-background-50/25 text-background-50 text-xs font-label font-semibold whitespace-nowrap hover:bg-background-50/10 transition-colors"
                >
                  开始浏览
                </Link>
              </div>
            </div>
            <div className="lg:col-span-5 grid grid-cols-3 gap-3">
              {[
                { value: `${latestAll.length}`, label: "在库影视" },
                { value: `${latestAll.filter((t) => t.year === 2026).length}`, label: "2026 新作" },
                { value: "7", label: "影视题材" },
              ].map((stat) => (
                <div key={stat.label} className="rounded-md bg-background-50/8 border border-background-50/12 p-3 md:p-4">
                  <p className="font-label text-xl md:text-2xl font-black text-accent-400">{stat.value}</p>
                  <p className="mt-1 text-[11px] md:text-xs text-background-200/80">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </SiteLayout>
    </>
  );
}